import {
  FISCAL_KIND_LABELS,
  formatCuit,
  ISSUER_CONDITION_LABELS,
  RECEPTOR_CONDITION_LABELS,
  VAT_RATE_LABELS,
  type FiscalDocumentDTO,
  type FiscalLineItem,
} from '@bifeai/shared';

/**
 * Factura (o nota) de la marca a una franquicia en hoja A4: emisor, letra y código, receptor con CUIT,
 * renglones, IVA discriminado (letra A), período del servicio, CAE con su vencimiento y el QR de ARCA.
 */
const money = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 });
const qtyFmt = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 3 });
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
const m = (cents: number) => money.format(cents / 100);
const ymd = (d: string | null) => (d ? d.split('-').reverse().join('/') : '');
const RATE: Record<string, number> = { '21': 0.21, '10.5': 0.105, '27': 0.27, '5': 0.05, '2.5': 0.025, '0': 0 };

function qtyText(l: FiscalLineItem): string {
  if (l.unit === 'WEIGHT') return `${qtyFmt.format(l.qty / 1000)} kg`;
  if (l.unit === 'UNIT') return `${l.qty} u.`;
  return '1';
}

function rowsHtml(doc: FiscalDocumentDTO): string {
  const lines: FiscalLineItem[] = doc.lines?.length
    ? doc.lines
    : [
        {
          description: doc.reason ?? FISCAL_KIND_LABELS[doc.kind],
          qty: 1,
          unit: 'SERVICE',
          unitPriceCents: doc.totalCents,
          totalCents: doc.totalCents,
          vatRate: null,
        },
      ];
  // Factura A: precios sin IVA y la alícuota de cada renglón. B y C: precios finales.
  const net = doc.letter === 'A';
  return lines
    .map((l) => {
      const r = net && l.vatRate ? (RATE[l.vatRate] ?? 0) : 0;
      const unit = net ? Math.round(l.unitPriceCents / (1 + r)) : l.unitPriceCents;
      const total = net ? Math.round(l.totalCents / (1 + r)) : l.totalCents;
      const per = l.unit === 'WEIGHT' ? '/kg' : '';
      return `<tr><td>${esc(l.description)}</td><td class="n">${qtyText(l)}</td><td class="n">${m(unit)}${per}</td>${
        net ? `<td class="n">${l.vatRate ? esc(VAT_RATE_LABELS[l.vatRate]) : '—'}</td>` : ''
      }<td class="n">${m(total)}</td></tr>`;
    })
    .join('');
}

export function franchiseInvoiceHtml(doc: FiscalDocumentDTO, associated?: { label: string } | null): string {
  const i = doc.issuer;
  const net = doc.letter === 'A';
  const totals = net
    ? `<tr><td>Neto gravado</td><td class="n">${m(doc.netCents)}</td></tr>${doc.vatBreakdown
        .map((v) => `<tr><td>IVA ${esc(VAT_RATE_LABELS[v.rate])}</td><td class="n">${m(v.amountCents)}</td></tr>`)
        .join('')}${doc.exemptCents ? `<tr><td>Exento</td><td class="n">${m(doc.exemptCents)}</td></tr>` : ''}${
        doc.untaxedCents ? `<tr><td>No gravado</td><td class="n">${m(doc.untaxedCents)}</td></tr>` : ''
      }`
    : '';
  const transparency =
    doc.letter === 'B' && doc.vatCents > 0
      ? `<p class="small">Régimen de Transparencia Fiscal al Consumidor (Ley 27.743) · IVA contenido: ${m(doc.vatCents)}</p>`
      : '';
  const service =
    doc.concept !== 1
      ? `<div class="period">Período facturado: ${ymd(doc.serviceFrom)} al ${ymd(doc.serviceTo)} · Vencimiento del pago: ${ymd(doc.paymentDue)}</div>`
      : '';
  const status =
    doc.status === 'AUTHORIZED'
      ? ''
      : `<div class="warn">${doc.status === 'PENDING' ? 'PENDIENTE DE AUTORIZACIÓN EN ARCA' : 'RECHAZADO POR ARCA'} · NO VÁLIDO COMO COMPROBANTE</div>`;
  return `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${esc(FISCAL_KIND_LABELS[doc.kind])} ${doc.letter} ${esc(doc.displayNumber ?? '')}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font: 11pt/1.35 system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif; color: #111; margin: 0; }
  .head { display: grid; grid-template-columns: 1fr 70px 1fr; border: 1px solid #111; }
  .head > div { padding: 10px 12px; }
  .letter { border-left: 1px solid #111; border-right: 1px solid #111; text-align: center; }
  .letter b { display: block; font-size: 34pt; line-height: 1; }
  .letter small { font-size: 8pt; }
  .biz { font-size: 14pt; font-weight: 700; margin-bottom: 4px; }
  .kind { font-size: 15pt; font-weight: 700; }
  .box { border: 1px solid #111; border-top: 0; padding: 8px 12px; display: grid; grid-template-columns: 1fr 1fr; gap: 2px 16px; }
  .period { border: 1px solid #111; border-top: 0; padding: 6px 12px; font-size: 10pt; }
  table { width: 100%; border-collapse: collapse; }
  .items { margin-top: 12px; }
  .items th { text-align: left; border-bottom: 1px solid #111; padding: 6px 4px; font-size: 9.5pt; }
  .items td { padding: 5px 4px; border-bottom: 1px solid #ddd; }
  .n { text-align: right; white-space: nowrap; }
  .foot { display: grid; grid-template-columns: 1fr 260px; gap: 16px; margin-top: 14px; align-items: start; }
  .totals td { padding: 3px 4px; }
  .totals .grand td { font-size: 13pt; font-weight: 700; border-top: 1px solid #111; padding-top: 6px; }
  .cae { display: flex; gap: 14px; align-items: center; margin-top: 18px; border-top: 1px solid #111; padding-top: 10px; }
  .cae img { width: 110px; height: 110px; }
  .small { font-size: 9pt; color: #444; }
  .warn { margin-top: 8px; padding: 6px 10px; border: 2px solid #b00; color: #b00; font-weight: 700; text-align: center; }
</style></head><body>
  <div class="head">
    <div>
      <div class="biz">${esc(i.legalName)}</div>
      ${i.address ? `<div>${esc(i.address)}</div>` : ''}
      <div>${esc(ISSUER_CONDITION_LABELS[i.ivaCondition])}</div>
    </div>
    <div class="letter"><b>${doc.letter}</b><small>Cód. ${String(doc.cbteTipo).padStart(3, '0')}</small></div>
    <div>
      <div class="kind">${esc(FISCAL_KIND_LABELS[doc.kind].toUpperCase())}</div>
      <div>Punto de venta y número: <strong>${esc(doc.displayNumber ?? 'sin número')}</strong></div>
      <div>Fecha de emisión: ${ymd(doc.issueDate)}</div>
      <div>CUIT: ${esc(formatCuit(i.cuit))}</div>
      ${i.grossIncomeId ? `<div>Ingresos brutos: ${esc(i.grossIncomeId)}</div>` : ''}
      ${i.activityStart ? `<div>Inicio de actividades: ${ymd(i.activityStart)}</div>` : ''}
    </div>
  </div>
  <div class="box">
    <div><strong>Razón social:</strong> ${esc(doc.receptorName ?? '')}</div>
    <div><strong>CUIT:</strong> ${esc(formatCuit(doc.docNro))}</div>
    <div><strong>Condición frente al IVA:</strong> ${esc(RECEPTOR_CONDITION_LABELS[doc.receptorCondition])}</div>
    <div><strong>Condición de venta:</strong> Cuenta corriente</div>
    ${doc.receptorAddress ? `<div style="grid-column: 1 / -1"><strong>Domicilio:</strong> ${esc(doc.receptorAddress)}</div>` : ''}
    ${associated ? `<div style="grid-column: 1 / -1"><strong>Comprobante asociado:</strong> ${esc(associated.label)}</div>` : ''}
  </div>
  ${service}
  ${doc.environment === 'HOMO' ? '<div class="warn">COMPROBANTE DE PRUEBA (HOMOLOGACIÓN) · SIN VALIDEZ FISCAL</div>' : ''}
  ${status}
  <table class="items">
    <thead><tr><th>Detalle</th><th class="n">Cantidad</th><th class="n">Precio${net ? ' (sin IVA)' : ''}</th>${net ? '<th class="n">IVA</th>' : ''}<th class="n">Subtotal</th></tr></thead>
    <tbody>${rowsHtml(doc)}</tbody>
  </table>
  <div class="foot">
    <div>${transparency}${doc.reason && doc.kind !== 'INVOICE' ? `<p><strong>Motivo:</strong> ${esc(doc.reason)}</p>` : ''}</div>
    <table class="totals">${totals}<tr class="grand"><td>TOTAL</td><td class="n">${m(doc.totalCents)}</td></tr></table>
  </div>
  <div class="cae">
    ${doc.qrDataUrl ? `<img src="${doc.qrDataUrl}" alt="QR de ARCA">` : ''}
    <div>
      <div><strong>CAE:</strong> ${esc(doc.cae ?? '—')}</div>
      <div><strong>Vencimiento del CAE:</strong> ${ymd(doc.caeDueDate) || '—'}</div>
      <div class="small">Comprobante autorizado por ARCA · emitido con BifeAI</div>
    </div>
  </div>
</body></html>`;
}

/** Abre el diálogo de impresión (o "Guardar como PDF") con la factura en A4. */
export function printFranchiseInvoice(doc: FiscalDocumentDTO, associated?: { label: string } | null): void {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  document.body.appendChild(frame);
  const d = frame.contentDocument;
  if (!d || !frame.contentWindow) {
    frame.remove();
    return;
  }
  d.open();
  d.write(franchiseInvoiceHtml(doc, associated));
  d.close();
  const win = frame.contentWindow;
  const cleanup = () => setTimeout(() => frame.remove(), 1000);
  win.addEventListener('afterprint', cleanup, { once: true });
  setTimeout(() => {
    win.focus();
    win.print();
    setTimeout(cleanup, 60_000);
  }, 150);
}
